import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { BackgroundBeams } from '../components/BackgroundBeams';
import {
    FaRocket,
    FaUsers,
    FaBriefcase,
    FaMapMarkerAlt,
    FaClock,
    FaCode,
    FaLightbulb,
    FaHeart,
    FaWhatsapp,
    FaTimes,
    FaCheckCircle,
    FaCloudUploadAlt,
    FaUser,
    FaEnvelope,
    FaPhone,
    FaFileAlt
} from 'react-icons/fa';
import './CareersPage.css';

const Careers = () => {
    const [showWhatsAppTooltip, setShowWhatsAppTooltip] = useState(false);
    const [showApplicationModal, setShowApplicationModal] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState('');
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [resumeFileName, setResumeFileName] = useState('');
    const [validationError, setValidationError] = useState('');
    const fileInputRef = useRef(null);

    const [applicationForm, setApplicationForm] = useState({
        fullName: '',
        email: '',
        phone: '',
        position: '',
        experience: '',
        coverLetter: '',
    });

    // WhatsApp configuration
    const whatsappNumber = '+917600236927';
    const whatsappMessage = 'Hi! I am interested in joining the DEPLOY-X team.';
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

    // Auto-show tooltip after 3 seconds
    React.useEffect(() => {
        const tooltipTimer = setTimeout(() => {
            setShowWhatsAppTooltip(true);
            // Auto-hide after 5 seconds
            setTimeout(() => setShowWhatsAppTooltip(false), 5000);
        }, 3000);
        return () => clearTimeout(tooltipTimer);
    }, []);

    const openPositions = [
        {
            title: 'Full Stack Developer',
            department: 'Engineering',
            location: 'Remote / Navi Mumbai, India',
            type: 'Full-time',
            icon: <FaCode />
        },
        {
            title: 'AI/ML Engineer',
            department: 'Engineering',
            location: 'Remote / Navi Mumbai, India',
            type: 'Full-time',
            icon: <FaRocket />
        },
        {
            title: 'UI/UX Designer',
            department: 'Design',
            location: 'Remote',
            type: 'Contract',
            icon: <FaLightbulb />
        },
        {
            title: 'Digital Marketing Specialist',
            department: 'Marketing',
            location: 'Navi Mumbai, India',
            type: 'Full-time',
            icon: <FaUsers />
        }
    ];

    const benefits = [
        {
            icon: <FaRocket />,
            title: 'Growth Culture',
            description: 'We invest in your professional growth through mentorship and learning resources.'
        },
        {
            icon: <FaHeart />,
            title: 'Wellness First',
            description: 'Comprehensive health benefits and a focus on work-life balance for every team member.'
        },
        {
            icon: <FaBriefcase />,
            title: 'Remote-First',
            description: 'Work from where you are most productive. We embrace flexibility and asynchronous work.'
        },
        {
            icon: <FaLightbulb />,
            title: 'Innovation Lab',
            description: 'Dedicated time for side projects and experimenting with cutting-edge technologies.'
        }
    ];

    const handleApplyClick = (positionTitle) => {
        setSelectedPosition(positionTitle);
        setApplicationForm(prev => ({ ...prev, position: positionTitle }));
        setFormSubmitted(false);
        setResumeFileName('');
        setValidationError('');
        setShowApplicationModal(true);
        document.body.style.overflow = 'hidden';
    };

    const handleCloseModal = () => {
        setShowApplicationModal(false);
        setFormSubmitted(false);
        setSelectedPosition('');
        setResumeFileName('');
        setApplicationForm({
            fullName: '',
            email: '',
            phone: '',
            position: '',
            experience: '',
            coverLetter: '',
        });
        document.body.style.overflow = 'auto';
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setApplicationForm(prev => ({ ...prev, [name]: value }));
        if (validationError) setValidationError('');
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setResumeFileName(e.target.files[0].name);
            if (validationError) setValidationError('');
        }
    };

    const handleSubmitApplication = (e) => {
        e.preventDefault();

        // Custom validation check
        const { fullName, email, phone, position, experience } = applicationForm;
        if (!fullName.trim() || !email.trim() || !phone.trim() || !position || !experience || !resumeFileName) {
            setValidationError('Please fill in all required fields before submitting.');
            return;
        }

        setValidationError('');
        // Simulate form submission
        setFormSubmitted(true);
    };

    return (
        <motion.div
            className="careers-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <SEO
                title="Careers at DEPLOY-X | Join Our Innovative Team"
                description="Build the future of AI and digital solutions with us. Explore open positions and learn about our culture and benefits at DEPLOY-X."
                pathname="/careers"
                keywords={['careers', 'job openings', 'join deploy-x', 'software engineer jobs India', 'remote tech jobs']}
            />

            <Navbar />

            <main>
                {/* Hero Section */}
                <section className="careers-hero">
                    <BackgroundBeams />
                    <div className="hero-overlay"></div>
                    <div className="container">
                        <motion.div
                            className="careers-hero-content"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className="section-label">Grow With Us</span>
                            <h1>Shape the Future of <span className="gradient-text">Digital Innovation</span></h1>
                            <p>
                                We're looking for passionate individuals to help us build the next generation of AI-powered solutions. Join our mission to transform businesses globally.
                            </p>
                            <motion.button
                                className="btn-primary"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => document.getElementById('open-positions')?.scrollIntoView({ behavior: 'smooth' })}
                            >
                                View Open Positions
                            </motion.button>
                        </motion.div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="benefits-section">
                    <div className="container">
                        <div className="section-header">
                            <span className="section-label">Why Join Us</span>
                            <h2>Our Perks & Culture</h2>
                            <p>We believe happy people build better products. Here's what we offer.</p>
                        </div>

                        <div className="benefits-grid">
                            {benefits.map((benefit, index) => (
                                <motion.div
                                    key={index}
                                    className="benefit-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <div className="benefit-icon">{benefit.icon}</div>
                                    <h3>{benefit.title}</h3>
                                    <p>{benefit.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Open Positions Section */}
                <section className="jobs-section" id="open-positions">
                    <div className="container">
                        <div className="section-header">
                            <span className="section-label">Current Openings</span>
                            <h2>Find Your Dream Role</h2>
                            <p>Ready to make an impact? Explore our current job opportunities.</p>
                        </div>

                        <div className="jobs-container">
                            {openPositions.map((job, index) => (
                                <motion.div
                                    key={index}
                                    className="job-card"
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <div className="job-info">
                                        <h3>{job.title}</h3>
                                        <div className="job-meta">
                                            <span><FaBriefcase /> {job.department}</span>
                                            <span><FaMapMarkerAlt /> {job.location}</span>
                                            <span><FaClock /> {job.type}</span>
                                        </div>
                                    </div>
                                    <motion.button
                                        className="apply-btn"
                                        whileHover={{ scale: 1.1 }}
                                        onClick={() => handleApplyClick(job.title)}
                                    >
                                        Apply Now
                                    </motion.button>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="cta-section">
                    <div className="container">
                        <motion.div
                            className="cta-wrapper"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                        >
                            <h2>Didn't find what you were looking for?</h2>
                            <p>
                                We're always looking for talented people to join our network. Send us your resume and we'll keep you in mind for future roles.
                            </p>
                            <motion.button
                                className="btn-primary"
                                whileHover={{ scale: 1.05 }}
                                onClick={() => handleApplyClick('General Application')}
                            >
                                Send Your Resume
                            </motion.button>
                        </motion.div>
                    </div>
                </section>
            </main>

            <Footer />

            {/* ========== Job Application Modal ========== */}
            <AnimatePresence>
                {showApplicationModal && (
                    <motion.div
                        className="application-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleCloseModal}
                    >
                        <motion.div
                            className="application-modal"
                            initial={{ opacity: 0, y: 60, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 60, scale: 0.95 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button className="modal-close-btn" onClick={handleCloseModal}>
                                <FaTimes />
                            </button>

                            {!formSubmitted ? (
                                <>
                                    <div className="modal-header">
                                        <span className="modal-label">Job Application</span>
                                        <h2>Apply for <span className="gradient-text">{selectedPosition}</span></h2>
                                        <p>Fill in your details below and we'll review your application.</p>
                                    </div>

                                    <form className="application-form" onSubmit={handleSubmitApplication}>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="fullName">
                                                    <FaUser className="field-icon" /> Full Name
                                                </label>
                                                <input
                                                    type="text"
                                                    id="fullName"
                                                    name="fullName"
                                                    placeholder="Enter your full name"
                                                    value={applicationForm.fullName}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="email">
                                                    <FaEnvelope className="field-icon" /> Email Address
                                                </label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    placeholder="Enter your email"
                                                    value={applicationForm.email}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="phone">
                                                    <FaPhone className="field-icon" /> Phone Number
                                                </label>
                                                <input
                                                    type="tel"
                                                    id="phone"
                                                    name="phone"
                                                    placeholder="Enter your phone number"
                                                    value={applicationForm.phone}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="experience">
                                                    <FaBriefcase className="field-icon" /> Years of Experience
                                                </label>
                                                <select
                                                    id="experience"
                                                    name="experience"
                                                    value={applicationForm.experience}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="" disabled>Select experience</option>
                                                    <option value="fresher">Fresher (0-1 year)</option>
                                                    <option value="junior">Junior (1-3 years)</option>
                                                    <option value="mid">Mid-Level (3-5 years)</option>
                                                    <option value="senior">Senior (5-8 years)</option>
                                                    <option value="lead">Lead (8+ years)</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="position">
                                                <FaCode className="field-icon" /> Position
                                            </label>
                                            <select
                                                id="position"
                                                name="position"
                                                value={applicationForm.position}
                                                onChange={handleInputChange}
                                            >
                                                <option value="" disabled>Select a position</option>
                                                {openPositions.map((pos, idx) => (
                                                    <option key={idx} value={pos.title}>{pos.title}</option>
                                                ))}
                                                <option value="General Application">General Application</option>
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label>
                                                <FaFileAlt className="field-icon" /> Upload Resume
                                            </label>
                                            <div
                                                className="file-upload-area"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    accept=".pdf,.doc,.docx"
                                                    onChange={handleFileChange}
                                                    style={{ display: 'none' }}
                                                />
                                                <FaCloudUploadAlt className="upload-icon" />
                                                {resumeFileName ? (
                                                    <p className="file-name">{resumeFileName}</p>
                                                ) : (
                                                    <>
                                                        <p>Drag & drop your resume here, or <span>browse</span></p>
                                                        <p className="file-hint">PDF, DOC, DOCX (max 5MB)</p>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="coverLetter">
                                                <FaFileAlt className="field-icon" /> Cover Letter (Optional)
                                            </label>
                                            <textarea
                                                id="coverLetter"
                                                name="coverLetter"
                                                rows="4"
                                                placeholder="Tell us why you'd be a great fit for this role..."
                                                value={applicationForm.coverLetter}
                                                onChange={handleInputChange}
                                            ></textarea>
                                        </div>

                                        <AnimatePresence>
                                            {validationError && (
                                                <motion.div
                                                    className="form-validation-error"
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    <span className="error-icon">⚠️</span>
                                                    <span>{validationError}</span>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <motion.button
                                            type="submit"
                                            className="submit-application-btn"
                                            whileHover={{ scale: 1.03 }}
                                            whileTap={{ scale: 0.97 }}
                                        >
                                            Submit Application
                                        </motion.button>
                                    </form>
                                </>
                            ) : (
                                <motion.div
                                    className="application-success"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                                >
                                    <div className="success-icon-wrapper">
                                        <FaCheckCircle className="success-icon" />
                                    </div>
                                    <h2>Application Submitted!</h2>
                                    <p>
                                        Thank you for applying for <strong>{selectedPosition}</strong> at DEPLOY-X.
                                    </p>
                                    <p className="success-detail">
                                        Our team will carefully review your resume and match it with available positions. You will be notified via email once we have an update on your application.
                                    </p>
                                    <motion.button
                                        className="btn-primary"
                                        onClick={handleCloseModal}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        Close
                                    </motion.button>
                                </motion.div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* WhatsApp Floating Button */}
            <div className="whatsapp-float-container">
                <AnimatePresence>
                    {showWhatsAppTooltip && (
                        <motion.div
                            className="whatsapp-tooltip"
                            initial={{ opacity: 0, x: 20, scale: 0.8 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 20, scale: 0.8 }}
                            transition={{ duration: 0.3 }}
                        >
                            <button className="tooltip-close" onClick={() => setShowWhatsAppTooltip(false)}>
                                <FaTimes />
                            </button>
                            <p className="tooltip-text">
                                <strong>Careers Query?</strong><br />
                                Chat with our HR team!
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.a
                    href={whatsappURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-button"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 1 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onMouseEnter={() => setShowWhatsAppTooltip(true)}
                    onMouseLeave={() => setShowWhatsAppTooltip(false)}
                >
                    <FaWhatsapp className="whatsapp-icon" />
                    <span className="pulse-ring"></span>
                    <span className="pulse-ring pulse-ring-delay"></span>
                </motion.a>
            </div>
        </motion.div>
    );
};

export default Careers;

